import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  Webhook,
  List,
  Loader2,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Info
} from "lucide-react";
import { format, parseISO } from "date-fns";

export default function WebhookLogs() {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSuccess, setFilterSuccess] = useState("all");

  const fetchLogs = async () => {
    try {
      let url = `${API}/webhook-logs`;
      if (filterSuccess !== "all") {
        url += `?success=${filterSuccess === "success"}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data);
    } catch (error) {
      console.error("Error fetching logs:", error);
      toast.error("Failed to load webhook logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [token, filterSuccess]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="webhook-logs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Webhook Logs</h1>
          <p className="text-gray-500 mt-1">
            View webhook delivery history for check-in notifications
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filterSuccess} onValueChange={setFilterSuccess}>
            <SelectTrigger className="w-[150px]" data-testid="filter-select">
              <SelectValue placeholder="Filter status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="success">Success</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={() => {
              setLoading(true);
              fetchLogs();
            }}
            data-testid="refresh-logs-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="border-0 shadow-sm bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">Webhook Configuration</p>
              <p className="mt-1">
                Webhook settings (URL, API Token, UID) are now configured per-location. 
                Go to <strong>Locations</strong> to set up or modify webhook endpoints.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Status</TableHead>
                <TableHead>Member</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Payload</TableHead>
                <TableHead>Response</TableHead>
                <TableHead>Attempt</TableHead>
                <TableHead>Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.length > 0 ? (
                logs.map((log) => (
                  <TableRow key={log.id} data-testid={`webhook-log-${log.id}`}>
                    <TableCell>
                      {log.success ? (
                        <span className="flex items-center gap-1 text-green-600">
                          <CheckCircle2 className="h-4 w-4" />
                          Success
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-red-600">
                          <XCircle className="h-4 w-4" />
                          Failed
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{log.member_name}</TableCell>
                    <TableCell>{log.location_name}</TableCell>
                    <TableCell>
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                        uid: {log.request_payload?.uid || "-"}, code: {log.request_payload?.code?.slice(0, 6)}...
                      </code>
                    </TableCell>
                    <TableCell>
                      {log.response_status ? (
                        <span className={log.success ? "text-green-600" : "text-red-600"}>
                          {log.response_status}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>#{log.attempt_number}</TableCell>
                    <TableCell>
                      {format(parseISO(log.created_at), "MMM d, h:mm a")}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <List className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-gray-500">No webhook logs yet</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Logs will appear here after members check in at locations with webhooks configured
                    </p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
